import React from 'react';
import styled from 'styled-components';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHeaderCell,
  TableCell,
} from '../../Table';
import { RadioGridProps } from '../../../../types/questionForm';
import { RadioInput } from '../DynamicQuestionForm.styled';
import { Tooltip } from '../../Tooltip';

// Local interface for RadioGrid options
interface RadioGridOption {
  id: string | number;
  text: string;
  infotext?: string;
}

// Styled Components
const RadioGridContainer = styled.div`
  width: 100%;
  overflow: visible;
`;

const GridHeader = styled.div`
  padding: var(--spacing-2);
`;

const GridTitle = styled.h4`
  font-size: var(--font-size-16);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-900);
  margin: 0;
  margin-bottom: var(--spacing-2);
`;

const StyledRadioGridTable = styled(Table)`
  margin: 0;
  border: none;
  box-shadow: none;
  border-radius: 0;
  overflow: visible;

  & thead,
  & tbody,
  & tr {
    overflow: visible;
  }

  /* Override Table component's column width calculations */
  & table {
    table-layout: fixed;
  }

  /* Force equal width for all option columns */
  & col:first-child {
    width: 55% !important;
  }

  & col:not(:first-child) {
    width: 15% !important;
  }
`;

const PropertyCell = styled(TableCell)`
  font-weight: var(--font-weight-light);
  font-size: var(--font-size-16);
  color: var(--color-gray-700);
  padding: var(--spacing-3) var(--spacing-4);
  width: 40%;
`;

const OptionCell = styled(TableCell)`
  text-align: center;
  padding: var(--spacing-3) var(--spacing-2);
  width: 20%;
`;

const OptionHeaderCell = styled(TableHeaderCell)`
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-16);
  color: var(--color-gray-800);
  padding: var(--spacing-3) var(--spacing-2);
  width: 20%;
  max-width: 150px;
  position: relative;
  overflow: visible;
`;

const PropertyHeaderCell = styled(TableHeaderCell)`
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-16);
  color: var(--color-gray-800);
  text-transform: none;
  letter-spacing: normal;
  padding: var(--spacing-3) var(--spacing-4);
  width: 40%;
  min-width: 200px;
`;

const RadioGridComponent: React.FC<RadioGridProps> = ({
  question,
  value = {},
  onChange = () => {},
  answers = {},
  onAnswerChange = () => {},
}) => {
  // Helper function to create AnswerValue structure
  const createAnswerValue = (
    selectedOptionId: string | null = null,
    typedValue: string | null = null
  ) => ({
    selectedOptionId,
    typedValue,
  });

  const handleRadioChange = (
    subQuestionId: string,
    optionId: number | string
  ) => {
    console.log('Radio changed ::', subQuestionId, optionId);
    
    // Find the selected option to check if it has attachment
    const subQuestion = question?.subQuestions?.find(sq => sq.id === subQuestionId);
    const selectedOption = subQuestion?.options?.find(
      opt => String(opt.id) === String(optionId)
    );
    
    console.log('Selected option details:', {
      subQuestionId,
      optionId,
      has_attachment: selectedOption?.has_attachment,
      questionHasAttachment: question.hasAttachment
    });
    
    // Create AnswerValue structure for radio selection
    const answerValue = createAnswerValue(String(optionId), null);
    onAnswerChange(subQuestionId, answerValue);
  };

  // Get individual answer values from the answers object
  const getSubQuestionValue = (subQuestionId: string) => {
    const answer = answers[subQuestionId]?.value;
    // Extract selectedOptionId from AnswerValue structure
    const selectedOption = answer?.selectedOptionId || answer;
    
    // If no answer, check for default_value in subQuestion
    if (!selectedOption) {
      const subQuestion = question?.subQuestions?.find(sq => sq.id === subQuestionId);
      const defaultValue = (subQuestion as any)?.defaultValue;
      
      // Handle both formats of default_value
      if (defaultValue) {
        return defaultValue?.selectedOptionId || defaultValue;
      }
    }
    
    return selectedOption;
  };

  // Handle both existing Question type structure and the specific JSON structure from API
  const questionText = (question as any)?.question || question?.label || '';

  // Extract options - handle both formats
  let options: RadioGridOption[] = [];
  if (question?.options) {
    // Handle API JSON format with option_text
    if ((question as any).options[0]?.option_text !== undefined) {
      options = (question as any).options.map((opt: any) => ({
        id: opt.id,
        text: opt.option_text,
        infotext: opt.infotext,
      }));
    }
    // Handle existing QuestionOption format with label
    else if (question?.options?.[0]?.label !== undefined) {
      options = question.options.map((opt: any) => ({
        id: opt.value,
        text: opt.label,
        infotext: opt.infotext,
      }));
    }
  }

  // Extract subQuestions - handle both formats
  let subQuestions: Array<{ id: string; text: string }> = [];
  if ((question as any)?.subQuestions) {
    subQuestions = (question as any).subQuestions.map((sq: any) => ({
      id: sq.id,
      text: sq.question || sq.label || sq.text || '',
    }));
  } else if (question?.subQuestions) {
    subQuestions = question.subQuestions.map((sq: any) => ({
      id: sq.id,
      text: sq.label || (sq as any).question || sq.text || '',
    }));
  }

  // Don't render if we don't have the required data
  if (options.length === 0 || subQuestions.length === 0) {
    return (
      <RadioGridContainer>
        <GridHeader>
          <GridTitle>{questionText}</GridTitle>
        </GridHeader>
        <div
          style={{
            padding: 'var(--spacing-4)',
            textAlign: 'center',
            color: 'var(--color-gray-500)',
          }}
        >
          {options.length === 0 && 'No options available'}
          {subQuestions.length === 0 && 'No sub-questions available'}
        </div>
      </RadioGridContainer>
    );
  }

  return (
    <RadioGridContainer>
      {/* Table */}
      <StyledRadioGridTable variant="default" size="md" >
        {/* <colgroup>
          <col style={{ width: '40%' }} />
          {options.map((_, index) => (
            <col key={index} style={{ width: '20%' }} />
          ))}
        </colgroup> */}
        <TableHeader appearance="ghost">
          <TableRow appearance="ghost">
            <PropertyHeaderCell appearance="ghost">Property</PropertyHeaderCell>
            {options.map(option => (
              <OptionHeaderCell
                key={option.id}
                appearance="ghost"
                align="center"
                className=''
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 'var(--spacing-1)',
                    position: 'relative',
                  }}
                >
                  <span>{option.text}</span>
                  {option.infotext && (
                    <Tooltip
                      content={option.infotext}
                      position="top"
                      tooltipStyle={{
                        transform: 'translateX(-25%)',
                        left: '50%',
                      }}
                    />
                  )}
                </div>
              </OptionHeaderCell>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {subQuestions.map(subQuestion => (
            <TableRow key={subQuestion.id} appearance="ghost">
              <PropertyCell appearance="ghost">{subQuestion.text}</PropertyCell>
              {options.map(option => (
                <OptionCell
                  key={`${subQuestion.id}-${option.id}`}
                  appearance="ghost"
                  className=''
                >
                  <RadioInput
                    type="radio"
                    name={`question-${subQuestion.id}`}
                    value={option.id}
                    checked={
                      String(getSubQuestionValue(subQuestion.id)) ===
                      String(option.id)
                    }
                    disabled={question?.attributes?.readOnly || false}
                    onChange={() =>
                      handleRadioChange(subQuestion.id, option.id)
                    }
                    aria-label={`${subQuestion.text} - ${option.text}`}
                  />
                </OptionCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </StyledRadioGridTable>
    </RadioGridContainer>
  );
};

export default RadioGridComponent;
